import CheckIcon from "@mui/icons-material/Check";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddressInput from "_shared/email-addresses/_views/email-address-input";
import EmailAddress from "_shared/email-addresses/email-address";
import NameInput from "_shared/names/_views/name-input";
import Name from "_shared/names/name";
import RegistrationInfo, { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import {
  useAcceptOfficerInvitationMutation,
  useResendOfficerInvitationLinkMutation,
} from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import {
  InvitationLinkInvalidError,
  InvitationRequiresSignInError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import EntityOfficerInvitation, {
  InvalidEntityOfficerInvitationError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useLocation, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import PasswordInput, { PasswordIssues } from "users/auth/credentials/passwords/_views/password-input";
import Password from "users/auth/credentials/passwords/password";
import OfficerOnboardingProgress from "legal-entities/onboarding/_views/officer-onboarding-progress";

const InvitationForm = styled(Container)(({ theme }) => ({
  color: theme.palette.text.primary,
  maxWidth: 720,
  textAlign: "left",
  overflowWrap: "anywhere",
}));
const AccountCard = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));
const FormContainer = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(3),
}));
const FormElements = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));
const InvitationFormTitle = styled("h2")(({ theme }) => ({
  ...theme.typography.h5,
  margin: theme.spacing(0, 0, 1),
}));
const InvitationFormInstructions = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  "& a": {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
}));
const InvitationMessage = styled("blockquote")(({ theme }) => ({
  ...theme.typography.body2,
  backgroundColor: theme.palette.grey[50],
  borderLeft: `3px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  margin: theme.spacing(2, 0),
  padding: theme.spacing(2),
}));
const InvitationEmailInput = styled(EmailAddressInput)(() => ({
  width: "100%",
  "&:first-of-type": {
    marginRight: 0,
  },
}));
const InvitationNameInput = styled(NameInput)(() => ({
  flex: 1,
  minWidth: 0,
}));
const InvitationPasswordInput = styled(PasswordInput)(({ theme }) => ({
  minWidth: 0,
  width: `calc((100% - ${theme.spacing(1.5)}) / 2)`,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    "&:first-of-type": {
      marginRight: 0,
    },
  },
}));
const InvitationAcceptButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

type InvitationAcceptanceFormError = string | PasswordIssues;

type InvitationAcceptanceFormErrors = {
  general?: string;
  email?: string;
  name?: string;
  password?: PasswordIssues;
};

type EntityOfficerInvitationAcceptanceFormProps = {
  className?: string;
  invitation: EntityOfficerInvitation;
  /** The acceptance token from the emailed link's query string. Absent from links sent before #6363. */
  acceptanceToken?: string;
};

/** What a refused acceptance asks the page to show instead of the form, if anything. */
type AcceptanceRefusal = "needs-sign-in" | "link-refused";

function refusalOf(error: unknown): AcceptanceRefusal | undefined {
  if (error instanceof InvitationRequiresSignInError) return "needs-sign-in";
  if (error instanceof InvitationLinkInvalidError) return "link-refused";
  return undefined;
}

function messageOf(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : "";
  return message || fallback;
}

export default function EntityOfficerInvitationAcceptanceForm(
  props: Readonly<EntityOfficerInvitationAcceptanceFormProps>,
) {
  const { acceptanceToken, invitation, className } = props;

  const [invitationEmail, setInvitationEmail] = useState<EmailAddress>(invitation.registrationInfo.email);
  const [invitationName, setInvitationName] = useState<Name>(invitation.registrationInfo.name);
  const [invitationPassword, setInvitationPassword] = useState<Password>();
  const [formErrors, setFormErrors] = useState<InvitationAcceptanceFormErrors>();
  const [saving, setSaving] = useState<boolean>(false);
  // Set when accepting found the invitee's account can already sign in, or the API refused the
  // link's token: either way the form gives way to what the invitee can do instead.
  const [refusal, setRefusal] = useState<AcceptanceRefusal>();
  const [freshLinkSent, setFreshLinkSent] = useState(false);

  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [acceptOfficerInvitation] = useAcceptOfficerInvitationMutation();
  const [resendOfficerInvitationLink] = useResendOfficerInvitationLinkMutation();

  // Signed in, the session is what shows who is accepting, so there is nothing to type. Whether
  // it is the invitee's is the API's to decide; comparing addresses here only chooses what to offer.
  const signedInEmail = session.isLoggedIn ? session.user?.email?.value : undefined;
  const invitedEmail = invitation.registrationInfo.email?.value;
  const signedInAsInvitee =
    signedInEmail !== undefined && signedInEmail.toLowerCase() === invitedEmail?.toLowerCase();
  const linkUsable = Boolean(acceptanceToken) && refusal !== "link-refused";
  const acceptanceTokenForLink = acceptanceToken ?? "";

  function rebuildInvitation(
    emailAddress?: EmailAddress,
    name?: Name,
    password?: Password,
    errors?: InvitationAcceptanceFormErrors,
  ): EntityOfficerInvitation | undefined {
    if (!invitation || !formValid(errors) || !emailAddress || !name || !password) return;

    try {
      const invitationRegistrationInfo = RegistrationInfo.create({ params: { name, email: emailAddress, password } });
      const updatedInvitation = EntityOfficerInvitation.create({
        params: {
          entityName: invitation.entityName,
          entityLocation: invitation.entityLocation,
          title: invitation.title,
          registrationInfo: invitationRegistrationInfo,
          message: invitation.message,
          id: invitation.id,
          status: invitation.status,
        },
      });
      return updatedInvitation;
    } catch (error) {
      if (error instanceof InvalidEntityOfficerInvitationError || error instanceof InvalidRegistrationInfoError) {
        let errors: InvitationAcceptanceFormErrors = {};
        errors.general = error.message;
        setFormErrors(errors);
        return;
      }
      throw error;
    }
  }

  async function handleAcceptInvitation() {
    setSaving(true);

    const builtInvite = rebuildInvitation(invitationEmail, invitationName, invitationPassword);

    try {
      if (!builtInvite) throw new InvalidEntityOfficerInvitationError("Invitation is invalid");
      const entityAgreement = await acceptOfficerInvitation({
        invitation: builtInvite,
        session,
        acceptance: { signedIn: false, token: acceptanceTokenForLink },
      }).unwrap();

      navigate(`/entity-agreements/${entityAgreement.id}`, {
        state: { entityAgreement: entityAgreement },
      });
    } catch (error) {
      const refused = refusalOf(error);
      if (refused) {
        setRefusal(refused);
        return;
      }
      console.error(error);
      enqueueSnackbar((error as Error)?.message ?? "Error accepting invite", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function handleAcceptSignedIn() {
    setSaving(true);
    try {
      const entityAgreement = await acceptOfficerInvitation({
        invitation,
        session,
        acceptance: { signedIn: true },
      }).unwrap();
      navigate(`/entity-agreements/${entityAgreement.id}`, {
        state: { entityAgreement: entityAgreement },
      });
    } catch (error) {
      enqueueSnackbar(messageOf(error, "There was an error accepting your invitation."), { variant: "error" });
      setSaving(false);
    }
  }

  async function handleSendFreshLink() {
    const invitationId = invitation.id;
    if (!invitationId) return;
    setSaving(true);
    try {
      await resendOfficerInvitationLink({ id: invitationId }).unwrap();
      setFreshLinkSent(true);
    } catch (error) {
      enqueueSnackbar(messageOf(error, "We could not send a fresh link. Please try again."), { variant: "error" });
    }
    setSaving(false);
  }

  async function handleSignOut() {
    await session.logout();
    // The page reads the session once; loading it again is what shows it signed out.
    globalThis.location.reload();
  }

  function signInToAccept() {
    // Back to this page afterwards, where the session will accept. The token is not carried: a
    // signed-in acceptance does not need it.
    navigate("/login", { state: { from: { pathname: location.pathname } } });
  }

  function formValid(errors?: InvitationAcceptanceFormErrors): boolean {
    errors ??= formErrors;

    return (
      invitationEmail !== undefined &&
      invitationName !== undefined &&
      invitationPassword !== undefined &&
      !errors?.general &&
      !errors?.email &&
      !errors?.name &&
      !errors?.password
    );
  }

  function updateFormErrors(type: "email" | "name" | "password", error?: InvitationAcceptanceFormError) {
    const errors: InvitationAcceptanceFormErrors = { ...formErrors };

    if (type === "password") {
      errors.password = error as PasswordIssues;
    } else {
      errors[type] = error as string | undefined;
    }

    setFormErrors(errors);
    return errors;
  }

  function invitationAlreadyAcceptedMessage() {
    return (
      <InvitationForm className={className} maxWidth={false} disableGutters>
        <AccountCard elevation={0}>
          <InvitationFormTitle>Invitation Already Accepted</InvitationFormTitle>
          <InvitationFormInstructions>
            The invitation has already been accepted.
            <br />
            If you believe this is in error please contact{" "}
            <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
              support@attorneyhub
            </a>
            .
          </InvitationFormInstructions>
          <InvitationFormInstructions>You can close this tab</InvitationFormInstructions>
        </AccountCard>
      </InvitationForm>
    );
  }

  function notice(title: string, instructions: string, action?: React.ReactNode) {
    return (
      <InvitationForm className={className} maxWidth={false} disableGutters>
        <AccountCard elevation={0}>
          <InvitationFormTitle>{title}</InvitationFormTitle>
          <InvitationFormInstructions>{instructions}</InvitationFormInstructions>
          {action}
        </AccountCard>
      </InvitationForm>
    );
  }

  if (!invitation) return null;
  if (invitation.status === InvitationStatus.Accepted) return invitationAlreadyAcceptedMessage();

  const entityName = invitation.entityName.value;

  if (signedInEmail !== undefined && signedInAsInvitee) {
    return notice(
      "Accept your invitation",
      `You are signed in as ${signedInEmail}. Accept to set up ${entityName}.`,
      <InvitationAcceptButton
        id="officer-invitation-acceptance-submit-button"
        variant="contained"
        color="primary"
        disabled={saving}
        loading={saving}
        startIcon={<CheckIcon />}
        onClick={handleAcceptSignedIn}
      >
        Accept and Continue
      </InvitationAcceptButton>,
    );
  }

  if (signedInEmail !== undefined) {
    return notice(
      "This invitation is for someone else",
      `This invitation to set up ${entityName} was sent to ${invitedEmail}, but you are signed in as ${signedInEmail}. Sign out, then open the invitation again.`,
      <InvitationAcceptButton variant="contained" loading={false} startIcon={<LogoutIcon />} onClick={handleSignOut}>
        Sign Out
      </InvitationAcceptButton>,
    );
  }

  if (refusal === "needs-sign-in") {
    return notice(
      "Sign in to accept",
      `${invitedEmail} already has an Attorney Hub account. Sign in with it to accept this invitation to set up ${entityName}.`,
      <InvitationAcceptButton variant="contained" loading={false} startIcon={<LoginIcon />} onClick={signInToAccept}>
        Sign In
      </InvitationAcceptButton>,
    );
  }

  if (!linkUsable) {
    return notice(
      "This link can't be used to accept",
      freshLinkSent
        ? `We've sent a fresh link to ${invitedEmail}. Open it from that email to accept.`
        : `To accept your invitation to set up ${entityName}, open the link in your invitation email. We can send a fresh one to ${invitedEmail}.`,
      freshLinkSent ? undefined : (
        <InvitationAcceptButton
          variant="contained"
          disabled={saving}
          loading={saving}
          startIcon={<EmailOutlinedIcon />}
          onClick={handleSendFreshLink}
        >
          Email Me a Fresh Link
        </InvitationAcceptButton>
      ),
    );
  }

  return (
    <InvitationForm className={className} maxWidth={false} disableGutters>
      <OfficerOnboardingProgress activeStep={0} organizationName={invitation.entityName.value} />
      <AccountCard elevation={0}>
        <InvitationFormTitle>Create your account</InvitationFormTitle>
        <InvitationFormInstructions>
          You were invited to set up {invitation.entityName.value}
          {invitation.title?.value ? ` as ${invitation.title.value}` : " as its officer"}. Confirm your details and choose
          a password to continue.
        </InvitationFormInstructions>
        {invitation.message?.value && (
          <InvitationMessage>&ldquo;{invitation.message.value}&rdquo;</InvitationMessage>
        )}
        <FormContainer>
          <InvitationEmailInput
            idPrefix="officer-invitation-acceptance"
            variant="outlined"
            initialValue={invitationEmail}
            onChange={(updatedEmailAddress?: EmailAddress, error?: string) => {
              if (updatedEmailAddress) setInvitationEmail(updatedEmailAddress);
              const errors = updateFormErrors("email", error);
              rebuildInvitation(updatedEmailAddress, invitationName, invitationPassword, errors);
            }}
          />
          <FormElements>
            <InvitationNameInput
              idPrefix="officer-invitation-acceptance"
              variant="outlined"
              initialValue={invitationName}
              onChange={(updatedName?: Name, error?: string) => {
                if (updatedName) setInvitationName(updatedName);
                const errors = updateFormErrors("name", error);
                rebuildInvitation(invitationEmail, updatedName, invitationPassword, errors);
              }}
            />
            <InvitationPasswordInput
              idPrefix="officer-invitation-acceptance"
              variant="outlined"
              confirm={true}
              password={invitationPassword}
              onChange={(updatedPassword?: Password, error?: PasswordIssues) => {
                if (updatedPassword) setInvitationPassword(updatedPassword);
                const errors = updateFormErrors("password", error);
                rebuildInvitation(invitationEmail, invitationName, updatedPassword, errors);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === KeyboardKey.Enter && formValid()) {
                  void handleAcceptInvitation();
                }
              }}
            />
          </FormElements>
          <InvitationAcceptButton
            id="officer-invitation-acceptance-submit-button"
            variant="contained"
            color="primary"
            disabled={saving || !formValid()}
            loading={saving}
            startIcon={<CheckIcon />}
            onClick={handleAcceptInvitation}
          >
            Create Account and Continue
          </InvitationAcceptButton>
        </FormContainer>
      </AccountCard>
    </InvitationForm>
  );
}
